import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm, useFieldArray } from "react-hook-form";
import { z } from "zod";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { apiService } from "@/lib/api-service";
import { parseValidationErrors } from "@/lib/parse-validation-errors.util";
import { useAuth } from "@/lib/auth-context";
import { UserRole } from "@employee-management/common";
import type { OvertimeEntry } from "@/types/project.types";
import { User } from "lucide-react";

const resendFormSchema = z.object({
  notes: z.string().optional(),
  employees: z
    .array(
      z.object({
        employeeId: z.string(),
        hours: z.number().positive("Hours must be positive"),
        rate: z.number().optional(),
        total: z.number().optional(),
        notes: z.string().optional(),
      })
    )
    .optional(),
});

type ResendFormData = z.infer<typeof resendFormSchema>;

interface OvertimeResendDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  overtimeEntry: OvertimeEntry;
  onComplete: () => void;
}

export function OvertimeResendDialog({
  open,
  onOpenChange,
  overtimeEntry,
  onComplete,
}: OvertimeResendDialogProps) {
  const queryClient = useQueryClient();
  const { hasRole } = useAuth();

  // Determine if user can see financial information
  const canSeeFinancialInfo = hasRole(UserRole.MANAGER);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
    watch,
    setError,
    getFieldState,
  } = useForm<ResendFormData>({
    resolver: zodResolver(resendFormSchema),
    defaultValues: {
      notes: "",
      employees: overtimeEntry.employees.map((emp) => ({
        employeeId: emp.employeeId,
        hours: emp.hours,
        rate: emp.rate || 0,
        total: emp?.total || 0,
        notes: emp.notes || "",
      })),
    },
  });

  const { fields } = useFieldArray({
    control,
    name: "employees",
  });

  const resendMutation = useMutation({
    mutationFn: async (data: ResendFormData) => {
      const payload = {
        notes: data.notes,
        employees: data.employees?.map((employee) => ({
          ...employee,
          // If user can't see financial info, don't send rate/total
          ...(canSeeFinancialInfo ? {} : { rate: undefined, total: undefined }),
        })),
      };
      await apiService.patch(`/overtime/${overtimeEntry.id}/resend`, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["overtime", "project"] });
      queryClient.invalidateQueries({ queryKey: ["overtime", "pending"] });
      // Also invalidate the specific project to update its updatedAt timestamp
      if (overtimeEntry.project?.id) {
        queryClient.invalidateQueries({
          queryKey: ["project", overtimeEntry.project.id],
        });
      }
      reset();
      onComplete();
    },
    onError: (error: unknown) => {
      parseValidationErrors<ResendFormData>(error, setError, getFieldState);

      toast.error("Failed to resend overtime entry");
    },
  });

  const onSubmit = (data: ResendFormData) => {
    resendMutation.mutate(data);
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      reset();
    }
    onOpenChange(newOpen);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Resend Overtime Entry</DialogTitle>
          <DialogDescription>
            This overtime entry was rejected. You can resend it for approval
            with additional notes or clarifications.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Project Information */}
          <div>
            <Label className="text-sm font-medium">Project</Label>
            <div className="mt-1 text-sm text-muted-foreground">
              {overtimeEntry.project.name} - {overtimeEntry.project.clientName}
            </div>
          </div>

          {/* Date and Status */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium">Date</Label>
              <div className="mt-1 text-sm text-muted-foreground">
                {formatDate(overtimeEntry.date)}
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium">Current Status</Label>
              <div className="mt-1 text-sm text-red-600 font-medium">
                REJECTED
              </div>
            </div>
          </div>

          {/* Rejection Reason */}
          {overtimeEntry.rejectionComment && (
            <div>
              <Label className="text-sm font-medium ">Rejection Reason</Label>
              <div className="mt-1 bg-destructive/20 border border-destructive rounded-md p-3">
                <p className="text-red-700 text-sm">
                  {overtimeEntry.rejectionComment}
                </p>
              </div>
            </div>
          )}

          {/* Employees */}
          <div>
            <Label className="text-sm font-medium">Employees</Label>
            <div className="mt-2 space-y-2">
              {fields.map((field, index) => {
                const employee = overtimeEntry.employees.find(
                  (emp) => emp.employeeId === field.employeeId
                );
                return (
                  <div
                    key={field.id}
                    className="p-3 border rounded-lg space-y-3"
                  >
                    <div className="flex items-center gap-2">
                      <div className="p-2 rounded-md bg-muted flex items-center justify-center">
                        <User className="w-4 h-4" />
                      </div>
                      <div className="flex flex-col">
                        <span className="font-medium text-sm">
                          {employee?.employee.name}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {employee?.employee.email}
                        </span>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-4">
                      <div>
                        <Label
                          htmlFor={`employees.${index}.hours`}
                          className="text-sm font-medium"
                          required
                        >
                          Hours
                        </Label>
                        <Input
                          id={`employees.${index}.hours`}
                          type="number"
                          step="0.5"
                          min="0"
                          {...register(`employees.${index}.hours` as const, {
                            valueAsNumber: true,
                          })}
                          className="mt-1"
                        />
                        {errors.employees?.[index]?.hours && (
                          <p className="text-sm text-red-500 mt-1">
                            {errors.employees[index]?.hours?.message}
                          </p>
                        )}
                      </div>

                      {canSeeFinancialInfo && (
                        <div>
                          <Label className="text-sm font-medium text-muted-foreground">
                            Rate: ${employee?.employee.hourlyRate}/hr
                          </Label>
                          <div className="mt-1 text-sm text-muted-foreground">
                            Total: $
                            {(
                              field.hours * (employee?.employee.hourlyRate || 0)
                            ).toFixed(2)}
                          </div>
                        </div>
                      )}
                    </div>

                    <div>
                      <Label
                        htmlFor={`employees.${index}.notes`}
                        className="text-sm font-medium"
                      >
                        Notes
                      </Label>
                      <Textarea
                        id={`employees.${index}.notes`}
                        {...register(`employees.${index}.notes` as const)}
                        className="mt-1"
                        placeholder="Employee-specific notes..."
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Summary */}
          <div className="bg-muted/40 p-3 rounded-md">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Total Hours:</span>
                <span className="ml-2 font-medium">
                  {watch("employees")
                    ?.reduce((total, emp) => total + (emp.hours || 0), 0)
                    .toFixed(1)}
                  h
                </span>
              </div>
              {canSeeFinancialInfo && (
                <div>
                  <span className="text-muted-foreground">Total Amount:</span>
                  <span className="ml-2 font-medium">
                    $
                    {watch("employees")
                      ?.reduce((total, emp) => {
                        const employee = overtimeEntry.employees.find(
                          (e) => e.employeeId === emp.employeeId
                        );
                        return (
                          total +
                          emp.hours * (employee?.employee.hourlyRate || 0)
                        );
                      }, 0)
                      .toFixed(2)}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Additional Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes" className="text-sm font-medium">
              Additional Notes
            </Label>
            <Textarea
              id="notes"
              placeholder="Add any additional notes or clarifications for the resubmission..."
              {...register("notes")}
              className="min-h-[100px]"
            />
            {errors.notes && (
              <p className="text-sm text-red-500">{errors.notes.message}</p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={resendMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit(onSubmit)}
            disabled={resendMutation.isPending}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {resendMutation.isPending ? "Resending..." : "Resend for Approval"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
